import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./Stock.css";

const API_URL = process.env.REACT_APP_API_URL;

/* ---------- Custom Select ---------- */
const CustomSelect = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const displayValue =
    options.find((o) => o.value === value)?.label || placeholder;

  return (
    <div className="custom-select-container" ref={selectRef}>
      <div className="selected-value" onClick={() => setIsOpen(!isOpen)}>
        {displayValue}
        <span className="dropdown-arrow">{isOpen ? "▲" : "▼"}</span>
      </div>

      {isOpen && (
        <ul className="options-list">
          {options.map((opt, i) => (
            <li
              key={i}
              className={`option-item ${
                opt.value === value ? "selected" : ""
              }`}
              onClick={() => {
                onChange({ target: { value: opt.value } });
                setIsOpen(false);
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ---------- MAIN COMPONENT ---------- */
const Stock = () => {
  const [cars, setCars] = useState([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState("");
  const [selectedBudget, setSelectedBudget] = useState(null);
  const navigate = useNavigate();

  /* ---------- Budget Ranges ---------- */
  const budgetRanges = [
    { label: "₹1L – ₹2L", min: 100000, max: 200000 },
    { label: "₹2L – ₹3L", min: 200000, max: 300000 },
    { label: "₹3L – ₹4L", min: 300000, max: 400000 },
    { label: "₹4L – ₹5L", min: 400000, max: 500000 },
    { label: "₹5L – ₹7L", min: 500000, max: 700000 },
    { label: "₹7L – ₹10L", min: 700000, max: 1000000 },
    { label: "₹10L+", min: 1000000, max: Infinity },
  ];

  const manufacturers = [
    "Audi","BMW","Chevrolet","Citroen","Datsun","Fiat","Ford","Honda",
    "Hyundai","Jaguar","Jeep","Kia","Land Rover","Mahindra","Maruti Suzuki",
    "Mercedes-Benz","M G","Nissan","Renault","Skoda","Tata","Toyota","Volkswagen"
  ];

  const manufacturerOptions = [
    { label: "All Brands", value: "" },
    ...manufacturers.map((m) => ({ label: m, value: m })),
  ];

  /* ---------- Fetch Cars ---------- */
  useEffect(() => {
    axios
      .get(`${API_URL}/cars`)
      .then((res) => setCars(res.data))
      .catch((err) => console.error(err));
  }, []);

  /* ---------- Clear All Filters ---------- */
  const clearAll = () => {
    setSelectedManufacturer("");
    setSelectedBudget(null);
  };

  /* ---------- Filter Logic ---------- */
  const filteredCars = cars.filter((car) => {
    const price = parseFloat(car.price || 0) * 100000;

    const brandMatch =
      !selectedManufacturer ||
      car.name.toLowerCase().includes(selectedManufacturer.toLowerCase());

    const budgetMatch =
      !selectedBudget ||
      (price >= selectedBudget.min && price <= selectedBudget.max);

    return brandMatch && budgetMatch;
  });

  return (
    <div className="main-container">
      {/* ---------- TOP FILTER BAR ---------- */}
      <div className="top-filters-bar">
        <div className="filter-group">
          <label className="filter-label">Brand</label>
          <CustomSelect
            options={manufacturerOptions}
            value={selectedManufacturer}
            onChange={(e) => setSelectedManufacturer(e.target.value)}
            placeholder="All Brands"
          />
        </div>

        <div className="budget-box-container">
          {budgetRanges.map((range, i) => (
            <div
              key={i}
              className={`budget-box ${
                selectedBudget?.label === range.label ? "active" : ""
              }`}
              onClick={() => setSelectedBudget(range)}
            >
              {range.label}
            </div>
          ))}
        </div>

        {/* ---------- CLEAR ALL BUTTON ---------- */}
        {(selectedManufacturer || selectedBudget) && (
          <button className="clear-filter" onClick={clearAll}>
            Clear All
          </button>
        )}
      </div>

      {/* ---------- CONTENT ---------- */}
      <div className="content">
        <h1 className="page-title">PRE-LOVED CARS</h1>

        <div className="stock-cars-grid">
          {filteredCars.map((car, i) => (
            <div
              key={i}
              className="stock-car-card"
              onClick={() => navigate(`/car-details/${car._id}`)}
            >
              {/* IMAGE + BADGES */}
              <div className="image-wrapper">
                <img src={car.photo1} alt={car.name} />

                {car.booked && (
                  <div className="badge-top-right">BOOKED</div>
                )}

                <div className="badge-bottom">CARCRAZE ASSURED</div>
              </div>

              {/* DETAILS */}
              <h3 className="car-title">
                {car.year} {car.name} {car.model}
              </h3>

              <div className="car-specs">
                <span>{car.running} km</span>
                <span>{car.fuel}</span>
                <span>{car.transmission}</span>
                <span>{car.registration || "N/A"}</span>
              </div>

              <div className="price-row">
                <div className="price">₹{car.price} lakh</div>
                <div className="other-charges">+ other charges</div>
              </div>
            </div>
          ))}
        </div>

        {filteredCars.length === 0 && (
          <p className="no-cars">No cars found</p>
        )}
      </div>
    </div>
  );
};

export default Stock;
